// Importing necessary modules and utilities
import axios from "axios"
import HRRService from "./HRRService"
import AxiosConfig from "src/utilities/AxiosConfig"
import snackBar from "src/utilities/snackBar/SnackBar"

// Define the structure of an item in the report
interface Item {
    title: string
    key: string
    order: number
    isCensusSource: boolean // True when the value is sourced from the Vault77 census rather than the employee roster
}

// Define a minimal employee object type for clarity
interface EmployeeMiniTypes {
    [key: string]: any
    UserId: number
}

// Define a minimal census-record type for clarity (Vault77 census endpoint shape)
interface CensusDataMiniTypes {
    [key: string]: any
    EmployeeId: number
}

const HRRCrudService = {
    // Merges data from employee and census based on provided items
    mergeData(employee: EmployeeMiniTypes, census: Record<string, any>, items: Item[]): Record<string, string> {
        const merged: Record<string, string> = {}
        items.forEach(item => {
            merged[item.key] = item.isCensusSource ? (census[item.key] ?? "Unassigned") : (employee[item.key] ?? "Unassigned")
        })
        return merged
    },

    // Fetches census demographics (Gender/Ethnicity) for the whole org from the Vault77 census
    // endpoint in a SINGLE call, then merges them with the already-fetched employee roster in
    // memory (keyed by EmployeeId). Replaces the old secure-session + 100-record chunked grab.
    getCensusData: async (employees: EmployeeMiniTypes[]) => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/vault77/census`,
                AxiosConfig.configValue("min")
            )
            const censusData: CensusDataMiniTypes[] = res.data || []

            // Index census rows by EmployeeId for O(1) lookup during the merge.
            const censusByEmployeeId = new Map<number, CensusDataMiniTypes>()
            censusData.forEach(row => censusByEmployeeId.set(row.EmployeeId, row))

            const finalEmployees = employees.map(emp => {
                const census = censusByEmployeeId.get(emp.UserId) || {}
                return HRRCrudService.mergeData(emp, census, HRRService.state.selectedReport?.properties.items ?? [])
            })

            HRRService.manageState([
                { key: "employeeReport", value: finalEmployees },
                { key: "firstLoad", value: false },
                { key: "progress", value: 100 },
                { key: "reporting", value: false },
                { key: "finished", value: true }
            ])
        } catch (error) {
            snackBar({ type: "error", text: "Error fetching census data", timeout: 3000 })
            HRRService.cleanUp()
        }
    },

    // Main function to run a report, fetch and process data
    runReport: async () => {
        HRRService.manageState([
            { key: "fetchingEmployees", value: true },
            { key: "reporting", value: true },
            { key: "finished", value: false }
        ])

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/user/*`,
                AxiosConfig.configValue()
            )
            const allEmployees = HRRService.filterEmployeesFromReportFilterKeys(response.data.users)
            const employees = allEmployees.filter(emp => {
                const empNum = emp.EmployeeNumber && emp.EmployeeNumber.trim()
                return empNum !== "" && /^\d+$/.test(empNum)
            })

            const hasCensusSource = HRRService.state.selectedReport?.properties.items?.some(item => item.isCensusSource)
            if (!hasCensusSource && hasCensusSource !== false) {
                snackBar({ type: "error", text: "Selected report or its properties are missing.", timeout: 3000 })
                HRRService.cleanUp()
                return
            }

            if (hasCensusSource) {
                // Second call: pull census demographics for everyone and merge in memory.
                HRRService.manageState([
                    { key: "fetchingEmployees", value: false },
                    { key: "progress", value: 50 }
                ])
                await HRRCrudService.getCensusData(employees)
            } else {
                const finalEmployees = employees.map(emp => HRRCrudService.mergeData(emp, {}, HRRService.state.selectedReport?.properties.items ?? []))
                HRRService.manageState([
                    { key: "employeeReport", value: finalEmployees },
                    { key: "progress", value: 100 },
                    { key: "reporting", value: false },
                    { key: "fetchingEmployees", value: false },
                    { key: "finished", value: true }
                ])
            }
        } catch (error) {
            snackBar({ type: "error", text: "Error running report.", timeout: 3000 })
            HRRService.cleanUp()
        }
    }
}

export default HRRCrudService
